import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CardSaveBadge } from '../components/tenant/CardSaveBadge';
import { isImpersonationPathname, portalHref } from '../utils/portalPaths';
import GroupProductPreferencesCard from '../components/tenant/GroupProductPreferencesCard';
import PropertyNotificationBedroomPicker from '../components/tenant/PropertyNotificationBedroomPicker';
import QueensFacultyProgramPicker, {
  type QueensAcademicSelection,
} from '../components/tenant/QueensFacultyProgramPicker';
import { ChangeVerifiedPhone } from '../components/tenant/ChangeVerifiedPhone';
import ProspectGroupSection, { type ProspectGroupMembership } from '../components/tenant/ProspectGroupSection';
import ScrollForMoreHint from '../components/tenant/ScrollForMoreHint';
import QualificationLandingVideoSection from '../components/tenant/QualificationLandingVideoSection';
import { useTenantApiToken, useTenantProfile } from '../contexts/TenantProfileContext';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { useAutosave } from '../hooks/useAutosave';
import { useCardSaveBadges } from '../hooks/useCardSaveBadges';
import { ReferralCard } from '../components/tenant/ReferralCard';
import { submitProspectPreApplication } from '../services/tenantApi';
import { GOOGLE_ADS_EVENTS, trackGoogleAdsEvent } from '@shared/utils/google-ads';
import { formatPhoneForTenantPortalDisplay } from '../utils/phone-display';
import { academicsFromStored, queensAcademicsComplete, storedFromAcademics } from '../utils/queens-academics';

const SCHOOL_YEAR_OTHER = 'Other' as const;
const SCHOOL_YEAR_NA = 'NA' as const;

const SCHOOL_YEAR_OPTIONS = [
  { value: '1st', label: '1st year' },
  { value: '2nd', label: '2nd year' },
  { value: '3rd', label: '3rd year' },
  { value: '4th', label: '4th year' },
  { value: 'Graduate', label: 'Graduate' },
  { value: SCHOOL_YEAR_NA, label: 'N/A' },
  { value: SCHOOL_YEAR_OTHER, label: 'Other' },
] as const;

/** Read-only tenant and applicant summaries stay a comfortable width. */
const MY_INFORMATION_SHELL = 'mx-auto w-full max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl';

/** Prospect My Information uses the main column on laptop screens. */
const PROSPECT_INFORMATION_SHELL = 'mx-auto w-full max-w-lg md:max-w-3xl lg:max-w-none';

function uniqueBedroomRankings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const rankings: string[] = [];
  for (const value of values) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    rankings.push(value);
  }
  return rankings;
}

const SECTION_CARD =
  'rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 px-4 py-4 shadow-bento sm:px-6 sm:py-5';
const CARD_TITLE = 'pr-8 text-lg font-bold text-gray-900 dark:text-white';
const CARD_DESCRIPTION = 'mt-1 text-sm text-gray-500 dark:text-gray-400';

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return phone;
}

function phoneFromAuthUser(user: { phone_number?: string; name?: string; nickname?: string; sub?: string } | undefined): string {
  const fromSub =
    typeof user?.sub === 'string' && /^sms\|/i.test(user.sub) ? user.sub.replace(/^sms\|/i, '') : '';
  const candidates = [user?.phone_number, fromSub];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.replace(/\D/g, '').length >= 10) {
      return candidate;
    }
  }
  return '';
}

function emailFromAuthUser(user: { email?: string } | undefined): string {
  return typeof user?.email === 'string' ? user.email.trim() : '';
}

function verifiedViaFromProspectOrAuth(
  prospect: { verifiedVia?: 'phone' | 'email' } | null | undefined,
  user: { sub?: string; email?: string; phone_number?: string } | undefined,
): 'phone' | 'email' {
  if (prospect?.verifiedVia) return prospect.verifiedVia;
  const sub = user?.sub ?? '';
  if (/^sms\|/i.test(sub)) return 'phone';
  if (/^email\|/i.test(sub)) return 'email';
  const phone = phoneFromAuthUser(user);
  const email = emailFromAuthUser(user);
  if (phone && !email) return 'phone';
  if (email && !phone) return 'email';
  return phone ? 'phone' : 'email';
}

function isValidPhoneInput(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 10;
}

function splitDisplayName(displayName: string | null | undefined): { firstName: string; lastName: string } {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function combineFirstLastName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
}

function SchoolYearPicker({
  value,
  otherText,
  onChange,
  onOtherTextChange,
}: {
  value: string;
  otherText: string;
  onChange: (value: string) => void;
  onOtherTextChange: (text: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const selectedOption = SCHOOL_YEAR_OPTIONS.find((option) => option.value === value);
  const triggerLabel =
    value === SCHOOL_YEAR_OTHER
      ? otherText.trim()
        ? otherText.trim()
        : 'Other'
      : (selectedOption?.label ?? 'Select year');

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const preferredHeight = 280;
    const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;

    if (openDown) {
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        top: rect.bottom + 4,
        maxHeight: Math.max(120, Math.min(preferredHeight, spaceBelow - 12)),
        overflowY: 'auto',
        zIndex: 60,
      });
      return;
    }

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      bottom: window.innerHeight - rect.top + 4,
      maxHeight: Math.max(120, Math.min(preferredHeight, spaceAbove - 12)),
      overflowY: 'auto',
      zIndex: 60,
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id="prospect-school-year"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={`flex w-full items-center justify-between rounded-md border bg-white px-3 py-2.5 text-base text-gray-900 transition-colors hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:bg-gray-950 dark:text-gray-100 dark:hover:border-white/20 ${
          isOpen ? 'border-gray-500 ring-1 ring-gray-400 dark:border-white/30' : 'border-gray-300 dark:border-white/15'
        }`}
      >
        <span className={selectedOption || value === SCHOOL_YEAR_OTHER ? '' : 'text-gray-500 dark:text-gray-400'}>{triggerLabel}</span>
        <svg
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul
          ref={menuRef}
          role="listbox"
          aria-labelledby="prospect-school-year-label"
          style={menuStyle}
          className="max-h-[min(280px,50vh)] overflow-y-auto rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 shadow-lg"
        >
          {SCHOOL_YEAR_OPTIONS.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2.5 text-left text-base transition-colors ${
                    isSelected ? 'bg-gray-100 font-medium text-gray-900 dark:bg-gray-800 dark:text-white' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-white/5'
                  }`}
                >
                  <span>{option.label}</span>
                  {isSelected && (
                    <svg className="h-4 w-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {value === SCHOOL_YEAR_OTHER && (
        <input
          type="text"
          id="prospect-school-year-other"
          value={otherText}
          onChange={(event) => onOtherTextChange(event.target.value)}
          placeholder="Enter your year of school"
          className="mt-2 w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400"
          autoComplete="off"
        />
      )}
    </div>
  );
}

function resolveSchoolYearForSubmit(selection: string, otherText: string): string {
  if (selection === SCHOOL_YEAR_OTHER) return otherText.trim();
  return selection;
}

function parseSchoolYearFromProspect(saved: string | null | undefined): { selection: string; otherText: string } {
  if (!saved) return { selection: '1st', otherText: '' };
  const known = SCHOOL_YEAR_OPTIONS.find((option) => option.value === saved);
  if (known) return { selection: saved, otherText: '' };
  return { selection: SCHOOL_YEAR_OTHER, otherText: saved };
}

interface ProspectPreApplicationPayload {
  name: string;
  email?: string;
  phone?: string;
  bedroomRankings: string[];
  generalNotifications: boolean;
  schoolYear: string;
  school: string;
  program: string;
  expectedGraduationTerm: string;
  expectedGraduationYear: string;
  referrerName: string;
  referrerSource: string;
}

function buildProspectPreApplicationPayload(input: {
  firstName: string;
  lastName: string;
  verifiedVia: 'phone' | 'email';
  email: string;
  phone: string;
  bedroomRankings: string[];
  generalNotifications: boolean;
  schoolYear: string;
  schoolYearOther: string;
  queensAcademics: QueensAcademicSelection;
  referrerName: string;
  referrerSource: string;
}): ProspectPreApplicationPayload {
  const stored = storedFromAcademics(input.queensAcademics);
  return {
    name: combineFirstLastName(input.firstName, input.lastName),
    ...(input.verifiedVia === 'phone'
      ? { email: input.email.trim() }
      : { phone: input.phone.trim() }),
    bedroomRankings: input.bedroomRankings,
    generalNotifications: input.generalNotifications,
    schoolYear: resolveSchoolYearForSubmit(input.schoolYear, input.schoolYearOther),
    school: stored.school,
    program: stored.program,
    expectedGraduationTerm: stored.expectedGraduationTerm,
    expectedGraduationYear: stored.expectedGraduationYear,
    referrerName: input.referrerName,
    referrerSource: input.referrerSource,
  };
}

function serializeProspectPreApplicationPayload(payload: ProspectPreApplicationPayload): string {
  return JSON.stringify(payload);
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-base text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

function ProspectInformationForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const impersonating = isImpersonationPathname(location.pathname);
  const { prospect, refreshProfile } = useTenantProfile();
  const { user } = useAuth0();
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const verifiedVia = verifiedViaFromProspectOrAuth(prospect, user);
  const verifiedPhone = prospect?.phone || phoneFromAuthUser(user);
  const verifiedEmail = prospect?.email || emailFromAuthUser(user);

  const initialName = splitDisplayName(prospect?.displayName);
  const [firstName, setFirstName] = useState(initialName.firstName);
  const [lastName, setLastName] = useState(initialName.lastName);
  const [email, setEmail] = useState(
    verifiedVia === 'phone' ? (prospect?.email ?? '') : '',
  );
  const [phone, setPhone] = useState(
    verifiedVia === 'email' && prospect?.phone ? formatPhoneDisplay(prospect.phone) : '',
  );
  const [bedroomRankings, setBedroomRankings] = useState<string[]>(() =>
    uniqueBedroomRankings([prospect?.primaryBedroom, prospect?.secondaryBedroom, prospect?.tertiaryBedroom]),
  );
  const [generalNotifications, setGeneralNotifications] = useState(prospect?.generalNotifications ?? false);
  const initialSchoolYear = parseSchoolYearFromProspect(prospect?.schoolYear);
  const [schoolYear, setSchoolYear] = useState(initialSchoolYear.selection);
  const [schoolYearOther, setSchoolYearOther] = useState(initialSchoolYear.otherText);
  const [queensAcademics, setQueensAcademics] = useState<QueensAcademicSelection>(() => academicsFromStored(prospect));
  const [referrerName, setReferrerName] = useState(prospect?.referralName ?? '');
  const [referrerSource, setReferrerSource] = useState(prospect?.referralSource ?? '');
  const [groupMembership, setGroupMembership] = useState<ProspectGroupMembership | null>(null);
  const [preferencesDone, setPreferencesDone] = useState<boolean | null>(null);
  const [formHydrated, setFormHydrated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formReady, setFormReady] = useState(false);

  useEffect(() => {
    if (prospect?.displayName) {
      const parsed = splitDisplayName(prospect.displayName);
      setFirstName(parsed.firstName);
      setLastName(parsed.lastName);
    }
    if (verifiedVia === 'phone' && prospect?.email) setEmail(prospect.email);
    if (verifiedVia === 'email' && prospect?.phone) setPhone(formatPhoneDisplay(prospect.phone));
    const nextBeds = uniqueBedroomRankings([
      prospect?.primaryBedroom,
      prospect?.secondaryBedroom,
      prospect?.tertiaryBedroom,
    ]);
    if (nextBeds.length > 0) setBedroomRankings(nextBeds);
    if (prospect?.generalNotifications !== undefined) {
      setGeneralNotifications(prospect.generalNotifications);
    }
    if (prospect?.schoolYear) {
      const parsed = parseSchoolYearFromProspect(prospect.schoolYear);
      setSchoolYear(parsed.selection);
      setSchoolYearOther(parsed.otherText);
    }
    setQueensAcademics(academicsFromStored(prospect));
  }, [
    prospect?.displayName,
    prospect?.email,
    prospect?.phone,
    prospect?.primaryBedroom,
    prospect?.secondaryBedroom,
    prospect?.tertiaryBedroom,
    prospect?.generalNotifications,
    prospect?.schoolYear,
    prospect?.school,
    prospect?.program,
    prospect?.expectedGraduationTerm,
    prospect?.expectedGraduationYear,
    verifiedVia,
  ]);

  useEffect(() => {
    if (!prospect?.personId) return;
    setReferrerName(prospect.referralName ?? '');
    setReferrerSource(prospect.referralSource ?? '');
  }, [prospect?.personId]);

  const canAutoSave = useMemo(() => {
    if (firstName.trim().length === 0 || lastName.trim().length === 0) return false;
    if (verifiedVia === 'phone') return email.trim().length > 0;
    return isValidPhoneInput(phone);
  }, [firstName, lastName, email, phone, verifiedVia]);

  const persistSnapshot = useMemo(
    () =>
      serializeProspectPreApplicationPayload(
        buildProspectPreApplicationPayload({
          firstName,
          lastName,
          verifiedVia,
          email,
          phone,
          bedroomRankings,
          generalNotifications,
          schoolYear,
          schoolYearOther,
          queensAcademics,
          referrerName,
          referrerSource,
        }),
      ),
    [
      firstName,
      lastName,
      verifiedVia,
      email,
      phone,
      bedroomRankings,
      generalNotifications,
      schoolYear,
      schoolYearOther,
      queensAcademics,
      referrerName,
      referrerSource,
    ],
  );

  const schoolYearDone = resolveSchoolYearForSubmit(schoolYear, schoolYearOther).trim().length > 0;
  const schoolDone = schoolYearDone && queensAcademicsComplete(queensAcademics);
  const notificationsDone = bedroomRankings.length > 0 || generalNotifications;
  const basicDetailsDone = canAutoSave;
  const groupPersonAdded = groupMembership?.hasAddedPerson === true;
  const onPreferencesComplete = useCallback((complete: boolean) => {
    setPreferencesDone(complete);
  }, []);
  const detailsComplete = schoolDone && basicDetailsDone && notificationsDone && groupPersonAdded;
  const informationComplete =
    detailsComplete && preferencesDone === true && groupPersonAdded;
  const [discoveryPromptOpen, setDiscoveryPromptOpen] = useState(false);
  const promptedWhileComplete = useRef(false);
  const profileBaselineReady = useRef(false);
  const profileStartedIncomplete = useRef(false);
  const onGroupMembership = useCallback((membership: ProspectGroupMembership) => {
    setGroupMembership((current) => {
      if (
        current?.hasAddedPerson === membership.hasAddedPerson
        && current.isLead === membership.isLead
        && current.pending === membership.pending
      ) {
        return current;
      }
      return membership;
    });
  }, []);

  useEffect(() => {
    if (!formHydrated || preferencesDone === null || !groupMembership) return;
    const cards = [basicDetailsDone, schoolDone, preferencesDone, notificationsDone, groupPersonAdded];
    setPageProgress('/my-information', {
      completed: cards.filter(Boolean).length,
      total: cards.length,
    });
  }, [
    basicDetailsDone,
    formHydrated,
    groupMembership,
    groupPersonAdded,
    notificationsDone,
    preferencesDone,
    schoolDone,
    setPageProgress,
  ]);

  useEffect(() => {
    setFormReady(true);
    setFormHydrated(true);
  }, []);

  const profileSave = useAutosave({
    snapshot: persistSnapshot,
    ready: formReady,
    enabled: canAutoSave,
    resetKey: prospect?.personId ?? 'self',
    save: async (snapshot, context) => {
      const payload = JSON.parse(snapshot) as ProspectPreApplicationPayload;
      await submitProspectPreApplication(getToken, payload, { keepalive: context.keepalive });
    },
    onSaved: () => setError(null),
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Unable to save your details.');
    },
  });

  const cardSlices = useMemo(() => ({
    details: JSON.stringify({ firstName, lastName, email, phone }),
    school: JSON.stringify({ schoolYear, schoolYearOther, queensAcademics }),
    notifications: JSON.stringify({ bedroomRankings, generalNotifications }),
    referral: JSON.stringify({ referrerName, referrerSource }),
  }), [
    bedroomRankings,
    email,
    firstName,
    generalNotifications,
    lastName,
    phone,
    queensAcademics,
    schoolYear,
    schoolYearOther,
    referrerName,
    referrerSource,
  ]);
  const cardSave = useCardSaveBadges(
    cardSlices,
    profileSave.status,
    formReady,
    prospect?.personId ?? 'self',
  );

  useEffect(() => {
    if (!formHydrated || preferencesDone === null || !groupMembership) return;
    if (!informationComplete) {
      promptedWhileComplete.current = false;
      setDiscoveryPromptOpen(false);
      return;
    }
    if (promptedWhileComplete.current) return;
    promptedWhileComplete.current = true;
    setDiscoveryPromptOpen(true);
  }, [formHydrated, groupMembership, informationComplete, preferencesDone]);

  useEffect(() => {
    if (impersonating || !formHydrated || !prospect?.personId) return;
    if (preferencesDone === null || !groupMembership) return;

    if (!profileBaselineReady.current) {
      profileBaselineReady.current = true;
      profileStartedIncomplete.current = !informationComplete;
      return;
    }
    if (!profileStartedIncomplete.current || !informationComplete) return;

    const dedupeKey = `google-ads-profile-complete:${prospect.personId}`;
    try {
      if (sessionStorage.getItem(dedupeKey) === '1') return;
      sessionStorage.setItem(dedupeKey, '1');
    } catch {
      // Still send once for this page view if storage is blocked.
    }
    profileStartedIncomplete.current = false;
    trackGoogleAdsEvent(GOOGLE_ADS_EVENTS.PROSPECT_PROFILE_COMPLETE, {
      site: 'portal',
      transaction_id: prospect.personId,
    });
  }, [
    formHydrated,
    groupMembership,
    impersonating,
    informationComplete,
    preferencesDone,
    prospect?.personId,
  ]);

  const fieldClass =
    'w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400';
  const readOnlyFieldClass =
    'w-full rounded-md border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-2.5 text-base text-gray-700 dark:text-gray-300';

  return (
    <div className="flex flex-col gap-4 pb-2 sm:pb-4">
      <header className={SECTION_CARD}>
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 1</p>
          <p className="shrink-0 text-right text-xs font-medium text-gray-500 dark:text-gray-400 sm:text-sm">
            Changes save automatically.
          </p>
        </div>
        <div className="mt-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">My Information</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base">
            Tell us how to reach you, where you go to school, and which homes you want to hear about.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 min-[880px]:grid-cols-3">
        <QualificationLandingVideoSection
          className="mb-0 h-full col-span-1 sm:col-span-2 min-[880px]:col-span-2"
          title="Welcome"
          description="A quick overview before you share your details."
          placeholderLabel="Qualification intro"
          titleClassName="text-xl font-bold text-gray-900 dark:text-white"
          descriptionClassName={CARD_DESCRIPTION}
          headerClassName="border-b border-gray-100 dark:border-white/5 px-4 py-4 sm:px-6 sm:py-5"
          bodyClassName="px-4 py-4 sm:px-6 sm:py-5"
          playerAspectClass="aspect-video"
        />

        <section className={`relative flex h-full flex-col sm:col-span-2 min-[880px]:col-span-1 ${SECTION_CARD}`}>
          <CardSaveBadge phase={cardSave.details} complete={basicDetailsDone} />
          <h2 className={CARD_TITLE}>Basic details</h2>
          <p className={CARD_DESCRIPTION}>How we reach you.</p>
          <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label htmlFor="prospect-first-name" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                First name
                <span className="ml-1 text-gray-500 dark:text-gray-400">*</span>
              </label>
              <input
                type="text"
                id="prospect-first-name"
                name="givenName"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                required
                autoComplete="given-name"
                autoFocus
                placeholder="First name"
                className={fieldClass}
              />
            </div>
            <div>
              <label htmlFor="prospect-last-name" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Last name
                <span className="ml-1 text-gray-500 dark:text-gray-400">*</span>
              </label>
              <input
                type="text"
                id="prospect-last-name"
                name="familyName"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                required
                autoComplete="family-name"
                placeholder="Last name"
                className={fieldClass}
              />
            </div>
          </div>

          {verifiedVia === 'email' ? (
            <div>
              <label htmlFor="prospect-email" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email
                <span className="ml-1 text-gray-500 dark:text-gray-400">*</span>
              </label>
              <input
                type="email"
                id="prospect-email"
                name="email"
                value={verifiedEmail}
                readOnly
                autoComplete="email"
                className={readOnlyFieldClass}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This is the email you just verified.</p>
            </div>
          ) : (
            <div>
              <label htmlFor="prospect-phone" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone Number
                <span className="ml-1 text-gray-500 dark:text-gray-400">*</span>
              </label>
              <input
                type="tel"
                id="prospect-phone"
                name="phone"
                value={verifiedPhone ? formatPhoneDisplay(verifiedPhone) : ''}
                readOnly
                autoComplete="tel"
                className={readOnlyFieldClass}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This is the number we text. Confirm a different number here instead of creating another account.
              </p>
              <ChangeVerifiedPhone onChanged={refreshProfile} />
            </div>
          )}

          {verifiedVia === 'email' ? (
            <div>
              <label htmlFor="prospect-phone-entry" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone Number
                <span className="ml-1 text-gray-500 dark:text-gray-400">*</span>
              </label>
              <input
                type="tel"
                id="prospect-phone-entry"
                name="phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                required
                autoComplete="tel"
                inputMode="tel"
                placeholder="Enter your phone number"
                className={fieldClass}
              />
            </div>
          ) : (
            <div>
              <label htmlFor="prospect-email-entry" className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email
                <span className="ml-1 text-gray-500 dark:text-gray-400">*</span>
              </label>
              <input
                type="email"
                id="prospect-email-entry"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                inputMode="email"
                placeholder="Enter your email address"
                className={fieldClass}
              />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/40 p-3 text-sm font-medium text-red-800">
              {error}
            </div>
          )}
          </div>
        </section>

        <div className="grid grid-cols-1 items-stretch gap-4 sm:col-span-2 min-[880px]:col-span-3 min-[880px]:grid-cols-3">
          <section className={`relative flex h-full min-h-0 min-w-0 flex-col min-[880px]:col-span-1 ${SECTION_CARD}`}>
            <CardSaveBadge phase={cardSave.school} complete={schoolDone} />
            <h2 className={CARD_TITLE}>School</h2>
            <p className={CARD_DESCRIPTION}>Year, faculty, program, and when you expect to graduate.</p>
            <div className="mt-4 space-y-4">
              <div>
                <label
                  id="prospect-school-year-label"
                  htmlFor="prospect-school-year"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Current Year of School
                </label>
                <SchoolYearPicker
                  value={schoolYear}
                  otherText={schoolYearOther}
                  onChange={setSchoolYear}
                  onOtherTextChange={setSchoolYearOther}
                />
              </div>
              <QueensFacultyProgramPicker
                className="grid grid-cols-1 gap-4"
                value={queensAcademics}
                onChange={setQueensAcademics}
              />
            </div>
          </section>

          <PropertyNotificationBedroomPicker
            className="flex h-full min-h-0 min-w-0 min-[880px]:col-span-1"
            layout="bento"
            complete={notificationsDone}
            savePhase={cardSave.notifications}
            rankings={bedroomRankings}
            generalPool={generalNotifications}
            onChange={({ rankings, generalPool }) => {
              setBedroomRankings(rankings);
              setGeneralNotifications(generalPool);
            }}
          />

          <ReferralCard
            className={`relative flex h-full min-h-0 min-w-0 flex-col min-[880px]:col-span-1 ${SECTION_CARD}`}
            titleClassName={CARD_TITLE}
            descriptionClassName={CARD_DESCRIPTION}
            fieldClass={fieldClass}
            savePhase={cardSave.referral}
            optional
            compactColumn
            referrerName={referrerName}
            referrerSource={referrerSource}
            onReferrerNameChange={setReferrerName}
            onReferrerSourceChange={setReferrerSource}
            nameId="my-info-referrer-name"
            sourceId="my-info-find-us-channel"
          />
        </div>

        <section className={`relative min-w-0 sm:col-span-2 min-[880px]:col-span-3 ${SECTION_CARD}`}>
          <ProspectGroupSection
            embedded
            embeddedClassName="pt-0"
            complete={groupPersonAdded}
            onMembership={onGroupMembership}
          />
        </section>

        <GroupProductPreferencesCard
          className="sm:col-span-2 min-[880px]:col-span-3"
          onCompleteChange={onPreferencesComplete}
          leadBedrooms={groupMembership?.isLead ? bedroomRankings : undefined}
        />

      </div>
      <ScrollForMoreHint />
      {discoveryPromptOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDiscoveryPromptOpen(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="discovery-step-title"
          >
            <h2 id="discovery-step-title" className="text-lg font-bold text-gray-900 dark:text-white">
              You&apos;re done this step.
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Move on to Discovery Call.</p>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                onClick={() => setDiscoveryPromptOpen(false)}
              >
                Stay here
              </button>
              <button
                type="button"
                autoFocus
                className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
                onClick={() => navigate(portalHref('/discovery-call', impersonating))}
              >
                Go to Discovery Call
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function MyInformationPage() {
  const { kind, tenant, applicant, prospect, loading } = useTenantProfile();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-700" />
      </div>
    );
  }

  if (kind === 'prospect') {
    return (
      <div className={`${PROSPECT_INFORMATION_SHELL} px-0 pb-2 sm:pb-4`}>
        <ProspectInformationForm />
      </div>
    );
  }

  if (kind === 'applicant') {
    const name = applicant?.displayName || '—';
    return (
      <div className={`${MY_INFORMATION_SHELL} py-2 sm:py-6`}>
        <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-bento">
          <div className="border-b border-gray-100 dark:border-white/5 px-4 py-4 sm:px-8 sm:py-6">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">My Information</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Details on the application we already have for you.</p>
          </div>
          <div className="space-y-5 px-4 py-4 sm:px-8 sm:py-6">
            <InfoRow label="Name" value={name} />
            <InfoRow label="Phone Number" value={formatPhoneForTenantPortalDisplay(applicant?.phone)} />
            {applicant?.email && <InfoRow label="Email" value={applicant.email} />}
          </div>
        </div>
      </div>
    );
  }

  const tenantName = [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ') || '—';
  const propertyLabel = tenant?.residentialLease?.propertyName
    ? tenant.residentialLease.propertyUnitCount > 1 && tenant.residentialLease.unitNumber
      ? `#${tenant.residentialLease.unitNumber} - ${tenant.residentialLease.propertyName}`
      : tenant.residentialLease.propertyName
    : null;

  return (
    <div className={`${MY_INFORMATION_SHELL} py-2 sm:py-6`}>
      <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-bento">
        <div className="border-b border-gray-100 dark:border-white/5 px-4 py-4 sm:px-8 sm:py-6">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">My Information</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base">The details we have on file for your account.</p>
        </div>
        <div className="space-y-5 px-4 py-4 sm:px-8 sm:py-6">
          <InfoRow label="Name" value={tenantName} />
          <InfoRow label="Phone Number" value={formatPhoneForTenantPortalDisplay(tenant?.phone)} />
          {propertyLabel && <InfoRow label="Home" value={propertyLabel} />}
        </div>
      </div>
    </div>
  );
}
